/**
 * Environment-aware logger utility
 *
 * Provides logging functions that only output in development mode.
 * In production, all logs are suppressed to prevent sensitive data exposure.
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'

/**
 * Logger object that conditionally logs based on environment
 */
export const logger = {
  /**
   * Log informational messages (only in development)
   */
  log: (...args: unknown[]): void => {
    if (isDevelopment) {
      console.log(...args)
    }
  },

  /**
   * Log debug messages (only in development)
   */
  debug: (...args: unknown[]): void => {
    if (isDevelopment) {
      console.debug(...args)
    }
  },

  /**
   * Log warning messages (only in development)
   */
  warn: (...args: unknown[]): void => {
    if (isDevelopment) {
      console.warn(...args)
    }
  },

  /**
   * Log error messages (always - errors should be visible for debugging)
   * Note: Sanitizes error objects to avoid exposing sensitive stack traces
   */
  error: (message: string, error?: unknown): void => {
    if (isDevelopment) {
      console.error(message, error)
    } else {
      // In production, log a sanitized version without full error details
      console.error(message)
    }
  },

  /**
   * Log information messages (only in development)
   */
  info: (...args: unknown[]): void => {
    if (isDevelopment) {
      console.info(...args)
    }
  },
}

export default logger
